import axios from "axios";

import { apiConfig } from "../config/authConfig";

import { IInternalResponse } from "../types/base";
import { IMaterialPrice, IMaterialPriceLight, IMaterialPriceSearchRequest } from "../types/material";
import { IMaterialOption } from "../types/list";

import { CredentialManager } from "../classes/credentialManager";
import moment from "moment";


export namespace MaterialPriceService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Material Price.
	 * @param data Data for a single material price.
	 **********/
	export function mapMaterialPrice(data: any) {
		let item: IMaterialPrice = {
			id: data.id,
			externalPriceId: data.externalPriceId,
			materialId: data.materialId,
			materialName: data.materialName,
			materialCategory: data.materialCategory,
			isHazardous: data.isHazardous,
			customerId: data.customerId,
			customerName: data.customerName,
			customerCategoryId: data.customerCategoryId,
			customerCategoryName: data.customerCategoryName,
			vendorId: data.vendorId,
			vendorName: data.vendorName,
			vendorUsesExternalPriceIds: data.vendorUsesExternalPriceIds,
			price: data.price,
			startDate: data.startDate,
			endDate: data.endDate,
			wasteType: data.wasteType,
			recycleMethod: data.recycleMethod,
			notes: data.notes,
			profile: data.profile,
			uomId: data.uomId,
			uomName: data.uomName,
			uomAbbreviation: data.uomAbbreviation,
			priceByContainer: data.priceByContainer,
			containerId: data.containerId,
			containerPrice: data.containerPrice,
			externalMaterialName: data.externalMaterialName,
			customerMaterialId: data.customerMaterialId,
			isGrossWeightMaterial: data.isGrossWeightMaterial,
			replacedById: data.replacedById
		}

		return item;
	}

	/**********
	 * Convert an array of api response objects to an array of Material Prices.
	 * @param data Data for multiple material prices.
	 **********/
	export function mapMaterialPrices(data: any) {
		let materials: IMaterialPrice[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				materials.push(mapMaterialPrice(item));
			})
		}

		return materials;
	}

	/**********
	 * Convert one api response object to one select option.
	 * @param data Data for a single material price.
	 **********/
	export function mapMaterialPriceToMaterialOption(data: any) {
		let item: IMaterialOption = {
			value: data.id,
			name: data.materialName,
			uomId: data.uomId,
			uom: data.uomName,
			uomAbbreviation: data.uomAbbreviation,
			priceByContainer: data.priceByContainer,
			containerId: data.containerId,
			containerPrice: data.containerPrice,
			tilsCode: data.materialId,
			customerId: data.customerId,
			vendorId: data.vendorId,
			isHazardous: data.isHazardous
		}

		return item;
	}

	/**********
	 * Convert an array of api response objects to an array of select options.
	 * @param data Data for multiple material prices.
	 **********/
	export function mapMaterialPricesToMaterialOptions(data: any) {
		let materials: IMaterialOption[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				materials.push(mapMaterialPriceToMaterialOption(item));
			})
		}

		return materials;
	}

	/**********
	 * Convert one row from the vendor pricing spreadsheet to one material price.
	 * @param data Excel data for a single material price.
	 **********/
	export function mapMaterialPriceFromVendorUpload(vendorId: number, data: any) {
		let item: IMaterialPriceLight = {
			id: parseInt(data[10]),
			vendorName: data[0],
			customerName: data[1],
			materialName: data[2],
			customerCategoryName: data[3],
			wasteType: data[4],
			recycleMethod: data[5],
			price: data[7],
			startDate: (data[9] != "" && data[9] != null) ? ExcelDateToJSDate(data[9]) : undefined,
		}

		return item;
	}

	// Helper
	function ExcelDateToJSDate(serial) {
		var utc_days = Math.floor(serial - 25568);
		var utc_value = utc_days * 86400;
		var date_info = new Date(utc_value * 1000);
		var fractional_day = serial - Math.floor(serial) + 0.0000001;
		var total_seconds = Math.floor(86400 * fractional_day);
		var seconds = total_seconds % 60;

		total_seconds -= seconds;

		var hours = Math.floor(total_seconds / (60 * 60));
		var minutes = Math.floor(total_seconds / 60) % 60;

		return new Date(date_info.getFullYear(), date_info.getMonth(), date_info.getDate(), hours, minutes, seconds);
	}

	/**********
	 * Convert rows from the vendor pricing spreadsheet to material prices.
	 * @param data Excel data for multiple material prices.
	 **********/
	export function mapMaterialPricesFromVendorUpload(vendorId: number, data: any) {
		let materials: IMaterialPriceLight[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				materials.push(mapMaterialPriceFromVendorUpload(vendorId, item));
			})
		}

		return materials;
	}

	/**********
	 * Validate all necessary data is present in each row before attempting to upload new pricing data.
	 * @param data Excel data for multiple material prices.
	 **********/
	export function validateMaterialPricesForUpload(data: IMaterialPriceLight[]): IInternalResponse[] {
		let responses: IInternalResponse[] = [];

		if (Array.isArray(data) && data.length > 0) {
			for (let i = 0; i < data.length; i++) {
				if (data[i].price == null) {//price
					responses.push({
						success: false,
						message: `Row ${i + 1} is missing price. Please update the row or remove it from the spreadsheet and manually add the price.`
					})
				}

				if (data[i].startDate == null) {//startDate
					responses.push({
						success: false,
						message: `Row ${i + 1} is missing effective date. Please update the row or remove it from the spreadsheet and manually add the price.`
					})				
				}

				if (parseInt(moment(data[i].startDate).format("yyyyMMDD")) <= parseInt(moment().format("yyyyMMDD"))) {//startDate
					responses.push({
						success: false,
						message: `Row ${i + 1} has an invalid effective date. Effective dates must be in the future. Please update the row or remove it from the spreadsheet and manually add the price.`
					})				
				}

				if (data[i].id == null) {//id
					responses.push({
						success: false,
						message: `Row ${i + 1} is missing the old price ID field. Please update the row or remove it from the spreadsheet and manually add the price.`
					})				
				}
			}
		}

		return responses;
	}

	/**********
	 * Gets all materials.
	 **********/
	export async function getMaterialPrices() {
		const response = axios.get(
			apiConfig.default + "/materialprice/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets one material price.
	 * @param id The material price's id
	 **********/
	export async function getMaterialPrice(id: string) {
		const response = axios.get(
			apiConfig.default + "/materialprice/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Updates one material price.
	 * @param materialId The old material price's id
	 * @param price: The new price
	 * @param endDate The last date the old price should remain effective
	 * @param isGrossWeightMaterial Indicates if the material is a gross weight material
	 **********/
	export async function updateMaterialPrice(materialId: number, price: number, endDate: Date, isGrossWeightMaterial: boolean) {
		const formattedEndDate = String(endDate.getFullYear()) + String(endDate.getMonth() + 1).padStart(2, '0') + String(endDate.getDate()).padStart(2, '0');
		const response = axios.put(
			`${apiConfig.default}/materialprice/update/${materialId}/${formattedEndDate}/${price}/${isGrossWeightMaterial ? "1" : "0"}`,
			"payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Updates one material price's end date, but does not create a replacement material price.
	 * @param materialId The material price's id
	 * @param endDate The last day the material price will be effective.
	 **********/
	export async function terminateMaterialPrice(materialId: number, endDate: Date) {
		const e = String(endDate.getFullYear()) + String(endDate.getMonth() + 1).padStart(2, '0') + String(endDate.getDate()).padStart(2, '0');

		const response = axios.put(
			`${apiConfig.default}/materialprice/terminate/${materialId}/${e}`,
			"payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets all material prices by shipment id.
	 **********/
	export async function getMaterialPricesByShipment(shipmentId: string) {
		const response = axios.get(
			apiConfig.default + "/materialprice/shipment/" + shipmentId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets all material prices by customer id.
	 **********/
	export async function getMaterialPricesForCustomer(customerId: number) {
		const response = axios.get(
			apiConfig.default + "/materialprice/customer/" + customerId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets all material prices for a customer vendor pair.
	 **********/
	export async function getMaterialPricesForLoadForm(customerId: number, vendorId: number) {
		const response = axios.get(
			apiConfig.default + "/materialprice/load/" + customerId + "/" + vendorId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets lists of values used for filtering the Material Price Search.
	 **********/
	export async function getMaterialPriceSearchValueSets() {
		const response = axios.get(
			apiConfig.default + "/materialprice/search/valuesets",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Searches material prices with user-provided criteria and returns paginated results.
	 * @param request The search and pagination criteria.
	 **********/
	export async function searchMaterialPrices(request: IMaterialPriceSearchRequest) {
		const response = axios.post(
			apiConfig.default + "/materialprice/search",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Add a new material price .
	 * @param details The material price details.
	 **********/
	export async function addMaterialPrice(details: IMaterialPrice) {
		const apiResponse = axios.put(
			apiConfig.default + "/materialprice",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a vendor's material pricing.
	 * @param materialPricing Data parsed from a vendor material price sheet.
	 **********/
	export async function uploadMaterialPricing(materialPricing: IMaterialPriceLight[]) {
		const apiResponse = axios.put(
			apiConfig.default + "/materialprice/bulk",
			materialPricing,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'application/json'
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Get a spreadsheet prepopulated with materials for venders fill out with pricing.
	 * @param vendorId.
	 **********/
	export async function getMaterialPriceSheet(vendorId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/materialprice/vendor/" + vendorId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'application/json'
				},
				responseType: "arraybuffer"
			}
		)

		return apiResponse;
	}

	/**********
	 * Exports a list of material price items using the same search criteria as the material search.
	 * @param request The search criteria. Page number and size will be modified to return all results.
	 **********/
	export async function exportMaterialPrices(request: IMaterialPriceSearchRequest) {
		const response = axios.post(
			apiConfig.default + "/materialprice/export",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'application/json'
				}
			}
		)

		return response;
	}
}
